import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { ref, get, set } from 'firebase/database';
import { auth, database } from '../firebase/config';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const isCreatingDipendente = React.useRef(false);

  // Funzione di login
  async function login(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    const userRef = ref(database, `users/${userId}`);
    const snapshot = await get(userRef);
    if (snapshot.exists()) {
      setUserRole(snapshot.val().ruolo);
      return { success: true, role: snapshot.val().ruolo };
    } else {
      await signOut(auth);
      throw new Error('Utente non trovato nel database');
    }
  }

  // Funzione di logout
  function logout() {
    setUserRole(null);
    return signOut(auth);
  }

  // Crea account dipendente (chiamata solo dall'admin)
  async function creaDipendente(email, dipendenteId) {
    const adminUser = auth.currentUser;
    isCreatingDipendente.current = true;

    // Password temporanea random sicura
    const caratteri = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
    const passwordTemp = Array.from(
      { length: 10 },
      () => caratteri[Math.floor(Math.random() * caratteri.length)]
    ).join('') + 'A1!';

    const userCredential = await createUserWithEmailAndPassword(auth, email, passwordTemp);
    const uid = userCredential.user.uid;

    await set(ref(database, `users/${uid}`), {
      ruolo: 'dipendente',
      dipendenteId: dipendenteId,
      email: email,
      createdAt: Date.now(),
    });

    // Ripristina la sessione admin
    await auth.updateCurrentUser(adminUser);
    isCreatingDipendente.current = false;
    return { uid, passwordTemp };
  }

  // Admin resetta la password di un dipendente
  async function resetPasswordDipendente(email) {
    await sendPasswordResetEmail(auth, email);
  }

  // Controlla lo stato di autenticazione
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (isCreatingDipendente.current) return;
      if (user) {
        const userRef = ref(database, `users/${user.uid}`);
        const snapshot = await get(userRef);
        if (snapshot.exists()) {
          setCurrentUser(user);
          setUserRole(snapshot.val().ruolo);
        } else {
          // Utente esiste in Firebase Auth ma NON nel DB → forza logout
          await signOut(auth);
          setCurrentUser(null);
          setUserRole(null);
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Funzione reset password (usata dal login)
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  const value = {
    currentUser,
    userRole,
    login,
    logout,
    resetPassword,
    creaDipendente,
    resetPasswordDipendente
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
